<template>
  <div class="view-form" :style="formBackgroundStyle">
    <!-- Company Logo -->
    <div v-if="formData.logoUrl" class="form-logo text-center mb-2">
      <img :src="formData.logoUrl" alt="Company Logo" style="max-height: 60px; max-width: 180px; object-fit: contain;" />
    </div>
    <!-- Header -->
    <b-card class="mb-2">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <h4 class="mb-1">View Form</h4>
          <p class="text-muted mb-0">Preview and manage form details</p>
        </div>
        <div>
          <b-button variant="outline-secondary" class="mr-2" @click="goBack">
            <feather-icon icon="ArrowLeftIcon" size="16" class="mr-1" />
            Back
          </b-button>
          <b-button variant="outline-primary" class="mr-2" @click="editForm">
            <feather-icon icon="EditIcon" size="16" class="mr-1" />
            Edit
          </b-button>
          <b-button variant="success" @click="shareForm">
            <feather-icon icon="Share2Icon" size="16" class="mr-1" />
            Share
          </b-button>
          <b-button variant="info" class="ml-2" @click="viewResponses">
            <feather-icon icon="ListIcon" size="16" class="mr-1" />
            View Responses
          </b-button>
        </div>
      </div>
    </b-card>

    <b-row>
      <!-- Form Details -->
      <b-col lg="8" md="12" class="mb-3">
        <b-card title="Form Preview" class="mb-3">
          <!-- Form Header -->
          <div class="form-header mb-4">
            <h3 class="text-center mb-2">{{ formData.title || 'Untitled Form' }}</h3>
            <p class="text-center text-muted mb-0">{{ formData.description || 'Form description' }}</p>
          </div>

          <!-- Questions List -->
          <div v-if="formData.questions.length === 0" class="empty-state text-center py-5">
            <feather-icon icon="FileTextIcon" size="48" class="text-muted mb-3" />
            <h5 class="text-muted">No questions in this form</h5>
            <p class="text-muted">This form doesn't have any questions yet</p>
          </div>

          <div
            v-for="(question, index) in formData.questions"
            :key="question.id"
            class="question-item mb-4"
          >
            <div class="question-card">
              <!-- Only show for non-header questions -->
              <div class="question-header d-flex align-items-center" v-if="question.type !== 'header'">
                <div class="question-number mr-2">
                  <b-badge variant="primary">{{ getQuestionNumber(index) }}</b-badge>
                </div>
                <div class="question-text">
                  {{ question.text }}<span v-if="question.required" class="text-danger ml-1">*</span>
                </div>
              </div>

              <div class="question-content">
                <template v-if="question.type !== 'header'">
                  <!-- Question Type Specific Display -->
                  <div class="question-display d-flex align-items-center">
                    <!-- MCQ Display -->
                    <div v-if="question.type === 'mcq'" class="mcq-display mr-3">
                      <b-form-select
                        :options="question.options.map((opt, i) => ({ value: i, text: opt }))"
                        placeholder="Select an option"
                        disabled
                      />
                    </div>
                    <!-- Radio Display -->
                    <div v-if="question.type === 'radio'" class="radio-display mr-3">
                      <b-form-radio-group disabled class="d-flex align-items-center">
                        <b-form-radio
                          v-for="(option, optionIndex) in question.options"
                          :key="optionIndex"
                          :value="optionIndex"
                          class="mr-2"
                        >
                          {{ option }}
                        </b-form-radio>
                      </b-form-radio-group>
                    </div>
                    <!-- Checkbox Display -->
                    <div v-if="question.type === 'checkbox'" class="checkbox-display mr-3">
                      <b-form-checkbox-group disabled class="d-flex align-items-center">
                        <b-form-checkbox
                          v-for="(option, optionIndex) in question.options"
                          :key="optionIndex"
                          :value="optionIndex"
                          class="mr-2"
                        >
                          {{ option }}
                        </b-form-checkbox>
                      </b-form-checkbox-group>
                    </div>
                    <!-- One Liner Display -->
                    <div v-if="question.type === 'one-liner'" class="one-liner-display mr-3">
                      <b-form-input
                        :placeholder="question.placeholder || 'Enter your answer'"
                        disabled
                      />
                    </div>
                    <!-- Fill in the Blanks Display -->
                    <div v-if="question.type === 'fill-blanks'" class="fill-blanks-display mr-3">
                      <div v-html="formatFillBlanksText(question.textWithBlanks)" class="mb-2"></div>
                      <small class="text-muted">Fill in the blank spaces above</small>
                    </div>
                    <!-- Brief Answer Display -->
                    <div v-if="question.type === 'brief'" class="brief-answer-display mr-3">
                      <b-form-textarea
                        placeholder="Enter your answer"
                        rows="4"
                        disabled
                      />
                      <small class="text-muted">
                        Expected word count: {{ question.wordCount || 'No limit' }}
                      </small>
                    </div>
                  </div>
                  <!-- Conditional text info below options -->
                  <div v-if="question.showConditionalText" class="conditional-text-info mt-2">
                    <small class="text-muted">
                      <feather-icon icon="InfoIcon" size="14" class="mr-1" />
                      Conditional text will appear when an option is selected
                    </small>
                  </div>
                </template>
                <template v-else>
                  <!-- Only show the styled header for header type -->
                  <div class="header-display">
                    <component 
                      :is="question.headerStyle || 'h2'"
                      :class="`text-${question.headerColor || 'primary'} mb-2`"
                    >
                      {{ question.text || 'Header Text' }}
                    </component>
                    <hr v-if="question.showDivider" :class="`border-${question.headerColor || 'primary'}`" />
                  </div>
                </template>
              </div>
            </div>
          </div>

          <!-- <b-row class="mb-2">
            <b-col md="6">
              <label>Requested By:</label>
              <div class="border-bottom">{{ formData.headerFields.requestedBy }}</div>
            </b-col>
            <b-col md="6">
              <label>Date of Request:</label>
              <div class="border-bottom">{{ formData.headerFields.dateOfRequest }}</div>
            </b-col>
          </b-row> -->
          <!-- Repeat for other pairs -->
        </b-card>
      </b-col>

      <!-- Form Information Sidebar -->
      <b-col lg="4" md="12">
        <b-card title="Form Information" class="mb-3">
          <div class="form-info">
            <div class="info-item mb-3">
              <label class="info-label">Form Title</label>
              <div class="info-value">{{ formData.title || 'Untitled Form' }}</div>
            </div>

            <div class="info-item mb-3">
              <label class="info-label">Description</label>
              <div class="info-value">{{ formData.description || 'No description' }}</div>
            </div>

            <div class="info-item mb-3">
              <label class="info-label">Status</label>
              <div class="info-value">
                <b-badge :variant="getStatusVariant(formData.status)">
                  {{ formData.status || 'Draft' }}
                </b-badge>
              </div>
            </div>

            <div class="info-item mb-3">
              <label class="info-label">Total Questions</label>
              <div class="info-value">{{ getQuestionCount(formData) }}</div>
            </div>

            <div class="info-item mb-3">
              <label class="info-label">Responses</label>
              <div class="info-value">{{ formData.responses || 0 }}</div>
            </div>

            <div class="info-item mb-3">
              <label class="info-label">Created</label>
              <div class="info-value">{{ formatDate(formData.createdAt) }}</div>
            </div>

            <div class="info-item mb-3">
              <label class="info-label">Last Modified</label>
              <div class="info-value">{{ formatDate(formData.updatedAt) }}</div>
            </div>

            <div class="info-item mb-3">
              <label class="info-label">Theme</label>
              <div class="info-value">
                <b-badge :variant="formData.theme || 'primary'">
                  {{ formData.theme || 'Primary' }}
                </b-badge>
              </div>
            </div>

            <div class="info-item mb-3">
              <label class="info-label">Anonymous Responses</label>
              <div class="info-value">
                <b-badge :variant="formData.allowAnonymous ? 'success' : 'secondary'">
                  {{ formData.allowAnonymous ? 'Allowed' : 'Not Allowed' }}
                </b-badge>
              </div>
            </div>

            <div class="info-item mb-3">
              <label class="info-label">Assigned To</label>
              <div class="info-value">
                <b-badge :variant="getAssignmentVariant(formData.assignedTo)">
                  {{ getAssignmentText(formData.assignedTo) }}
                </b-badge>
              </div>
            </div>

            <div v-if="formData.assignedTo === 'specific' && formData.specificUsers && formData.specificUsers.length > 0" class="info-item mb-3">
              <label class="info-label">Specific Users</label>
              <div class="info-value">
                <div v-for="userId in formData.specificUsers" :key="userId" class="mb-1">
                  <small>{{ getUserName(userId) }}</small>
                </div>
              </div>
            </div>
          </div>
        </b-card>

        <!-- Quick Actions -->
        <b-card title="Quick Actions">
          <div class="quick-actions">
            <b-button
              variant="outline-primary"
              block
              class="mb-2"
              @click="duplicateForm"
            >
              <feather-icon icon="CopyIcon" size="16" class="mr-1" />
              Duplicate Form
            </b-button>
            <b-button
              variant="outline-success"
              block
              class="mb-2"
              @click="exportForm"
            >
              <feather-icon icon="DownloadIcon" size="16" class="mr-1" />
              Export Form
            </b-button>
            <b-button
              variant="outline-info"
              block
              class="mb-2"
              @click="viewResponses"
            >
              <feather-icon icon="BarChart2Icon" size="16" class="mr-1" />
              View Responses
            </b-button>
            <b-button
              variant="outline-warning"
              block
              class="mb-2"
              @click="archiveForm"
            >
              <feather-icon icon="ArchiveIcon" size="16" class="mr-1" />
              Archive Form
            </b-button>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- Share Modal -->
    <b-modal
      v-model="showShareModal"
      title="Share Form"
      ok-title="Copy Link"
      cancel-title="Close"
      @ok="copyShareLink"
    >
      <div class="share-content">
        <b-form-group label="Form Link" label-for="share-link">
          <b-form-input
            id="share-link"
            :value="shareLink"
            readonly
          />
        </b-form-group>
        
        <b-form-group label="Embed Code" label-for="embed-code">
          <b-form-textarea
            id="embed-code"
            :value="embedCode"
            rows="3"
            readonly
          />
        </b-form-group>

        <div class="share-options">
          <h6>Share Options</h6>
          <b-form-checkbox v-model="shareOptions.allowAnonymous">
            Allow anonymous responses
          </b-form-checkbox>
          <b-form-checkbox v-model="shareOptions.requireEmail">
            Require email address
          </b-form-checkbox>
          <b-form-checkbox v-model="shareOptions.limitResponses">
            Limit responses
          </b-form-checkbox>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BButton,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormSelect,
  BFormRadioGroup,
  BFormRadio,
  BFormCheckboxGroup,
  BFormCheckbox,
  BRow,
  BCol,
  BBadge,
  BModal,
} from 'bootstrap-vue'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'
import FormsService from '@/services/forms-service'

export default {
  name: 'ViewForm',
  components: {
    BCard,
    BButton,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormSelect,
    BFormRadioGroup,
    BFormRadio,
    BFormCheckboxGroup,
    BFormCheckbox,
    BRow,
    BCol,
    BBadge,
    BModal,
    FeatherIcon,
  },
  data() {
    return {
      showShareModal: false,
      shareOptions: {
        allowAnonymous: true,
        requireEmail: false,
        limitResponses: false,
      },
      formData: {
        id: 1,
        title: 'Employee Feedback Survey',
        description: 'Annual employee satisfaction survey to gather feedback and improve workplace environment',
        status: 'Published',
        theme: 'primary',
        allowAnonymous: true,
        questions: [
          {
            id: 1,
            type: 'mcq',
            text: 'How satisfied are you with your work environment?',
            required: true,
            options: ['Very Satisfied', 'Satisfied', 'Neutral', 'Dissatisfied', 'Very Dissatisfied'],
          },
          {
            id: 2,
            type: 'radio',
            text: 'Would you recommend this company to others?',
            required: true,
            options: ['Yes', 'No', 'Maybe'],
          },
          {
            id: 3,
            type: 'checkbox',
            text: 'Which benefits do you value most?',
            required: false,
            options: ['Health Insurance', 'Paid Time Off', 'Flexible Hours', 'Remote Work', 'Professional Development'],
          },
          {
            id: 4,
            type: 'one-liner',
            text: 'What is your job title?',
            required: true,
            placeholder: 'Enter your job title',
          },
          {
            id: 5,
            type: 'fill-blanks',
            text: 'The work environment is ___ and the management is ___.',
            required: false,
            textWithBlanks: 'The work environment is ___ and the management is ___.',
          },
          {
            id: 6,
            type: 'brief',
            text: 'What improvements would you suggest for the workplace?',
            required: false,
            wordCount: 100,
          },
        ],
        responses: 45,
        createdAt: '2024-01-15T10:30:00Z',
        updatedAt: '2024-01-20T14:15:00Z',
        headerFields: {
          requestedBy: '',
          dateOfRequest: '',
          jobRequestTitle: '',
          department: '',
          location: '',
          requisitionNumber: ''
        },
      },
    }
  },
  computed: {
    shareLink() {
      return `${window.location.origin}/forms/${this.formData.id}`
    },
    embedCode() {
      return `<iframe src="${this.shareLink}" width="100%" height="600" frameborder="0"></iframe>`
    },
    formBackgroundStyle() {
      if (this.formData.backgroundType === 'color' && this.formData.backgroundColor) {
        return `background: ${this.formData.backgroundColor}`;
      } else if (this.formData.backgroundType === 'image' && this.formData.backgroundImageUrl) {
        return `background: url('${this.formData.backgroundImageUrl}') center/cover no-repeat`;
      }
      return '';
    },
  },
  methods: {
    async loadFormData() {
      if (this.$route.params.id) {
        try {
          const response = await FormsService.getFormById(this.$route.params.id)
          if (response.success && response.data) {
            this.formData = { ...response.data }
          } else {
            throw new Error('Form not found')
          }
        } catch (error) {
          this.$bvToast.toast('Form not found', {
            title: 'Error',
            variant: 'danger',
            solid: true,
          })
          this.$router.push({ name: 'tools-forms-list' })
        }
      }
    },
    goBack() {
      this.$router.go(-1)
    },
    editForm() {
      this.$router.push({ name: 'tools-forms-edit', params: { id: this.formData.id } })
    },
    shareForm() {
      this.showShareModal = true
    },
    copyShareLink() {
      navigator.clipboard.writeText(this.shareLink).then(() => {
        this.$bvToast.toast('Link copied to clipboard!', {
          title: 'Success',
          variant: 'success',
          solid: true,
        })
      })
    },
    async duplicateForm() {
      try {
        await FormsService.duplicateForm(this.formData.id)
        this.$bvToast.toast('Form duplicated successfully!', {
          title: 'Success',
          variant: 'success',
          solid: true,
        })
      } catch (error) {
        this.$bvToast.toast('Error duplicating form', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },
    exportForm() {
      this.$bvToast.toast('Form exported successfully!', {
        title: 'Success',
        variant: 'success',
        solid: true,
      })
    },
    viewResponses() {
      this.$router.push({ name: 'tools-forms-view-responses', params: { id: this.formData.id } })
    },
    async archiveForm() {
      this.$bvModal.msgBoxConfirm('Are you sure you want to archive this form?', {
        title: 'Please Confirm',
        size: 'sm',
        buttonSize: 'sm',
        okVariant: 'warning',
        okTitle: 'YES',
        cancelTitle: 'NO',
        footerClass: 'p-2',
        hideHeaderClose: false,
        centered: true,
      }).then(async value => {
        if (value) {
          try {
            await FormsService.archiveForm(this.formData.id)
            this.formData.status = 'archived'
            this.$bvToast.toast('Form archived successfully!', {
              title: 'Success',
              variant: 'success',
              solid: true,
            })
          } catch (error) {
            this.$bvToast.toast('Error archiving form', {
              title: 'Error',
              variant: 'danger',
              solid: true,
            })
          }
        }
      })
    },
    getQuestionTypeName(type) {
      const questionTypes = {
        header: 'Header',
        mcq: 'Multiple Choice',
        radio: 'Radio Button',
        checkbox: 'Checkbox',
        'one-liner': 'One Liner',
        'fill-blanks': 'Fill in the Blanks',
        brief: 'Brief Answer',
      }
      return questionTypes[type] || type
    },
    getQuestionTypeVariant(type) {
      const variants = {
        header: 'light',
        mcq: 'primary',
        radio: 'info',
        checkbox: 'warning',
        'one-liner': 'success',
        'fill-blanks': 'secondary',
        brief: 'dark',
      }
      return variants[type] || 'primary'
    },
    getStatusVariant(status) {
      const variants = {
        draft: 'secondary',
        published: 'success',
        archived: 'warning',
      }
      return variants[status && status.toLowerCase()] || 'secondary'
    },
    formatFillBlanksText(text) {
      if (!text) return ''
      return text.replace(/___/g, '<input type="text" class="form-control d-inline-block" style="width: 150px;" placeholder="Answer" disabled>')
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A'
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    getQuestionCount(formData) {
      return formData.questions && formData.questions.length ? formData.questions.length : 0
    },
    getAssignmentVariant(assignedTo) {
      const variants = {
        'all': 'primary',
        'managers': 'info',
        'employees': 'warning',
        'specific': 'success',
      }
      return variants[assignedTo] || 'secondary'
    },
    getAssignmentText(assignedTo) {
      const texts = {
        'all': 'All Users',
        'managers': 'Managers Only',
        'employees': 'Employees Only',
        'specific': 'Specific Users',
      }
      return texts[assignedTo] || assignedTo
    },
    getUserName(userId) {
      const userOptions = [
        { value: 'user1', text: 'John Doe (Manager)' },
        { value: 'user2', text: 'Jane Smith (Employee)' },
        { value: 'user3', text: 'Mike Johnson (Manager)' },
        { value: 'user4', text: 'Sarah Wilson (Employee)' },
        { value: 'user5', text: 'David Brown (Manager)' },
      ]
      const user = userOptions.find(u => u.value === userId)
      return user ? user.text : `User ${userId}`
    },
    getQuestionNumber(idx) {
      // Count only non-header questions up to idx
      return this.formData.questions.slice(0, idx + 1).filter(q => q.type !== 'header').length;
    },
  },
  async mounted() {
    await this.loadFormData()
  }
}
</script>

<style lang="scss" scoped>
.view-form {
  .question-card {
    // border: 1px solid #e0e6ed;
    // border-radius: 8px;
    // padding: 12px 18px;
    // background-color: #f8f9fa;
    // margin-bottom: 18px;
    background-color: fff;
  }
  .question-header {
    padding: 0 0 4px 0;
    margin-bottom: 0;
    font-size: 1.08rem;
  }
  .question-content {
    padding: 0;
  }
  .question-display {
    margin-bottom: 0;
    padding-bottom: 0;
  }
  .form-logo {
    margin-bottom: 10px;
  }
  .form-info {
    .info-item {
      .info-label {
        font-weight: 600;
        color: #5e5873;
        font-size: 14px;
        margin-bottom: 4px;
        display: block;
      }

      .info-value {
        color: #6e6b7b;
        font-size: 14px;
      }
    }
  }

  .quick-actions {
    .btn {
      text-align: left;
    }
  }

  .share-content {
    .share-options {
      margin-top: 20px;
      padding-top: 20px;
      border-top: 1px solid #e0e6ed;

      h6 {
        margin-bottom: 12px;
        color: #5e5873;
      }
    }
  }

  .empty-state {
    color: #6e6b7b;
  }
}

// Custom styles for fill in the blanks
.fill-blanks-display {
  input[type="text"] {
    border: 1px dashed #7367f0;
    background-color: #f8f9fa;
    margin: 0 4px;
  }
}

// Custom styles for header display
.header-display {
  text-align: left;
  padding: 10px 0;
  
  
  h1, h2, h3, h4 {
    margin-bottom: 10px;
    font-weight: 600;
  }
  
  hr {
    margin: 15px auto;
    width: 50%;
    opacity: 0.7;
  }
}

// Custom styles for conditional text info
.conditional-text-info {
  small {
    color: #17a2b8;
    font-style: italic;
  }
}

/* Mobile Responsive Styles */
@media (max-width: 768px) {
  .view-form {
    padding: 0.5rem;
  }
  
  .card {
    margin-bottom: 1rem;
  }
  
  .card-header {
    padding: 0.75rem;
  }
  
  .card-body {
    padding: 0.75rem;
  }
  
  .btn {
    font-size: 0.8rem;
    padding: 0.375rem 0.75rem;
    margin-bottom: 0.25rem;
  }
  
  .form-label {
    font-size: 0.9rem;
    margin-bottom: 0.25rem;
  }
  
  .form-control {
    font-size: 0.9rem;
  }
  
  .question-header {
    font-size: 1rem;
  }
  
  .question-display {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .question-display > div {
    margin-bottom: 0.5rem;
    width: 100%;
  }
  
  .d-flex.justify-content-between {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .d-flex.justify-content-between > div:last-child {
    margin-top: 0.5rem;
    width: 100%;
  }
  
  .d-flex.justify-content-between > div:last-child .btn {
    margin-right: 0.25rem;
    margin-bottom: 0.25rem;
  }
  
  .form-info .info-item {
    margin-bottom: 1rem;
  }
}

@media (max-width: 576px) {
  .view-form {
    padding: 0.25rem;
  }
  
  .card-header {
    padding: 0.5rem;
  }
  
  .card-body {
    padding: 0.5rem;
  }
  
  .btn {
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
  }
  
  .form-label {
    font-size: 0.8rem;
  }
  
  .form-control {
    font-size: 0.8rem;
  }
  
  .question-header {
    font-size: 0.9rem;
  }
  
  .question-display {
    flex-direction: column;
  }
  
  .question-display > div {
    margin-bottom: 0.5rem;
    width: 100%;
  }
  
  .d-flex.justify-content-between {
    flex-direction: column;
  }
  
  .d-flex.justify-content-between > div:last-child {
    margin-top: 0.5rem;
    width: 100%;
  }
  
  .d-flex.justify-content-between > div:last-child .btn {
    width: 100%;
    margin-right: 0;
    margin-bottom: 0.25rem;
  }
  
  .form-info .info-item {
    margin-bottom: 0.75rem;
  }
  
  .form-info .info-label {
    font-size: 0.8rem;
  }
  
  .form-info .info-value {
    font-size: 0.8rem;
  }
}
</style> 